import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Button, Input, Space, Tag, Typography } from 'antd';
import { CloseOutlined, EditOutlined, PlusOutlined, ProfileTwoTone, SearchOutlined } from '@ant-design/icons';
import { AppDispatch } from 'src/index';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import tileStyles from 'src/styles/tile.module.css';
import { Link, useParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';
import { addTemplateCompany, removeCompanyFromTemplate } from 'src/features/templates/templates.state';
import { useTemplateCompaniesSectionById } from 'src/features/templates/templates.hooks';
import { ContentTemplateCompany } from 'src/features/templates/templates.types';
import { fetchCompanies } from 'src/features/companies/companies.state';
import { useCompaniesValues } from 'src/features/companies/companies.hooks';

interface TemplateAddCollectionsProps {
  templateId: string;
  currentCollections: ContentTemplateCompany[];
}

const TemplateAddCollections: React.FC<TemplateAddCollectionsProps> = props => {
  const intl = useIntl();
  const dispatch: AppDispatch = useDispatch();
  const companies = useCompaniesValues();
  const [name, setName] = useState<string | undefined>(undefined);
  const currentCollectionIds = useMemo(() => props.currentCollections.map(c => c.id), [props.currentCollections]);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddButtonClick = useCallback(
    async (companyId: string) => {
      setIsLoading(true);
      await dispatch(addTemplateCompany({ templateId: props.templateId, data: { companyId } }));
      setIsLoading(false);
    },
    [dispatch, props.templateId]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await dispatch(fetchCompanies({}));
      setIsLoading(false);
    })();
  }, [dispatch]);
  useDebounce(
    () => {
      if (name !== undefined && !isLoading) {
        (async () => {
          setIsLoading(true);
          await dispatch(fetchCompanies({ name: name }));
          setIsLoading(false);
        })();
      }
    },
    300,
    [dispatch, name]
  );

  return (
    <>
      <Input
        autoFocus
        allowClear
        placeholder={intl.formatMessage({ id: 'placeholder.SearchVideo' })}
        prefix={<SearchOutlined />}
        onChange={e => {
          setName(e.target.value);
        }}
        style={{ marginBottom: 10 }}
      />
      <div className={styles.content}>
        <LoaderWrapper loading={isLoading}>
          {companies
            .filter(c => !currentCollectionIds.includes(c.id) && !c.disabled)
            .map(c => (
              <div key={c.id} className={tileStyles.listTile}>
                <Link to={`/content/collections/${c.id}/videos`} className={tileStyles.tileName}>
                  <Space>
                    <ProfileTwoTone />
                    {c.name}
                  </Space>
                </Link>
                <Space>
                  {c.disabled && (
                    <Tag color={'orange'}>
                      <FormattedMessage id={'text.archived'} />
                    </Tag>
                  )}
                  <Button type={'primary'} size='small' onClick={() => handleAddButtonClick(c.id)}>
                    <FormattedMessage id={'action.Add'} />
                  </Button>
                </Space>
              </div>
            ))}
        </LoaderWrapper>
      </div>
    </>
  );
};

const ContentTemplateCompaniesTab: React.FC = () => {
  const { templateId } = useParams() as { templateId: string };
  const [addMode, setAddMode] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const companies = useTemplateCompaniesSectionById(templateId);
  const dispatch: AppDispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const handleRemoveButtonClick = useCallback(
    async (companyId: string) => {
      setIsLoading(true);
      await dispatch(removeCompanyFromTemplate({ templateId, companyId }));
      setIsLoading(false);
    },
    [dispatch, templateId]
  );

  const handleAddButtonClick = useCallback(() => {
    setAddMode(m => !m);
  }, []);

  const handleEditButtonClick = useCallback(() => {
    setEditMode(m => !m);
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Компании</Typography.Title>
        <Space direction={'horizontal'}>
          {!editMode && (
            <Button type={addMode ? undefined : 'primary'} onClick={handleAddButtonClick}>
              {addMode ? <CloseOutlined /> : <PlusOutlined />}
            </Button>
          )}
          {!addMode && (
            <Button onClick={handleEditButtonClick}>{editMode ? <CloseOutlined /> : <EditOutlined />}</Button>
          )}
        </Space>
      </div>
      <div className={styles.content}>
        {addMode && <TemplateAddCollections templateId={templateId} currentCollections={companies} />}
        {!addMode && (
          <LoaderWrapper loading={isLoading}>
            {companies?.map(c => (
              <div key={c.id} className={tileStyles.listTile}>
                <Link to={`/commercial/companies/${c.id}`} className={tileStyles.tileName}>
                  <Space>
                    <ProfileTwoTone />
                    {c.name}
                  </Space>
                </Link>
                <Space>
                  {editMode && (
                    <ConfirmButton danger size='small' onClick={() => handleRemoveButtonClick(c.id)}>
                      <FormattedMessage id={'action.Remove'} />
                    </ConfirmButton>
                  )}
                </Space>
              </div>
            ))}
          </LoaderWrapper>
        )}
      </div>
    </div>
  );
};
export default ContentTemplateCompaniesTab;
